import React, { useCallback, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/Pagination';
import {
  useListScenariosQuery,
  useSetScenarioApprovalMutation,
} from '@/store/apiSlice';
import { resolveMediaUrl } from '@/lib/resolveMediaUrl';
import { Trophy } from 'lucide-react';

export interface ScenarioItem {
  id: string;
  name: string;
  description: string;
  severity?: string;
  severity_name?: string;
  category_name: string;
  type_name?: string;
  thumbnail_url: string;
  points?: number;
  type?: string;
  is_prepared?: boolean;
  is_approved?: boolean;
  /** Whether Tabletop Exercise (TTX) is enabled for this scenario. */
  ttx_enabled?: boolean;
}

function getDifficultyColor(difficulty: string) {
  switch (difficulty) {
    case 'Easy':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'Medium':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    case 'Hard':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
    case 'Expert':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  }
}

export function ScenarioList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [unapprovingId, setUnapprovingId] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const typeFilter = 'all';

  const page = searchParams.get('page');
  const approved = searchParams.get('approved');
  const ttxEnabled = searchParams.get('ttx_enabled');
  const isApprovedFilter =
    approved === 'true' || approved === '1'
      ? true
      : approved === 'false' || approved === '0'
        ? false
        : undefined;
  const ttxEnabledFilter =
    ttxEnabled === 'true' || ttxEnabled === '1'
      ? true
      : ttxEnabled === 'false' || ttxEnabled === '0'
        ? false
        : undefined;
  const {
    data: listResponse,
    isLoading,
    isFetching,
  } = useListScenariosQuery({
    q: searchTerm || undefined,
    is_approved: isApprovedFilter,
    ttx_enabled: ttxEnabledFilter,
    page: page ?? undefined,
  });
  const [setScenarioApproval] = useSetScenarioApprovalMutation();

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set(name, value);
      return params.toString();
    },
    [searchParams]
  );

  const data = (listResponse?.results ?? []) as ScenarioItem[];
  const currentPage = listResponse?.current_page ?? 1;
  const nextPage = listResponse?.next ?? null;
  const prevPage = listResponse?.previous ?? null;
  const totalPages = listResponse?.total_pages ?? 1;

  const handleUnapprove = async (scenarioId: string) => {
    setUnapprovingId(scenarioId);
    try {
      await setScenarioApproval({ id: scenarioId, is_approved: false }).unwrap();
    } catch (err) {
      console.error('Failed to unapprove scenario:', err);
    } finally {
      setUnapprovingId(null);
    }
  };

  const handleApprove = async (scenarioId: string) => {
    setApprovingId(scenarioId);
    try {
      await setScenarioApproval({ id: scenarioId, is_approved: true }).unwrap();
    } catch (err) {
      console.error('Failed to approve scenario:', err);
    } finally {
      setApprovingId(null);
    }
  };

  const filteredData = data.filter((scenario) => {
    const matchesSearch =
      scenario.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (scenario.category_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || scenario.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const handlePageClick = (url: string | null) => {
    if (!url) return;
    try {
      const parsedUrl = new URL(url);
      const pageParam = parsedUrl.searchParams.get('page');
      if (pageParam) {
        navigate(`?${createQueryString('page', pageParam)}`);
      }
    } catch {
      // ignore
    }
  };

  const pathname = '/scenarios';

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="md:col-span-2">
          <Input
            placeholder="Search scenarios..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>
      </div>
      {isLoading || isFetching ? (
        <div className="flex justify-center items-center py-12">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {filteredData.length > 0 ? (
            <>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredData.map((scenario) => (
                  <Card
                    key={scenario.id}
                    className="group overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                  >
                    <div className="relative h-40 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900">
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent z-10" />
                      <img
                        src={resolveMediaUrl(scenario.thumbnail_url) || scenario.thumbnail_url}
                        alt={scenario.name}
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                      />
                      <div className="absolute bottom-0 left-0 p-4 z-20 flex flex-wrap gap-2">
                        <Badge className={`${getDifficultyColor(scenario.severity_name || scenario.severity || '')} font-medium`}>
                          {scenario.severity_name || scenario.severity || 'N/A'}
                        </Badge>
                        {scenario.type_name && (
                          <Badge variant="secondary">{scenario.type_name}</Badge>
                        )}
                        {typeof scenario.ttx_enabled === 'boolean' && (
                          <Badge
                            className={
                              scenario.ttx_enabled
                                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 font-medium'
                                : 'bg-muted/80 text-muted-foreground font-normal'
                            }
                          >
                            TTX {scenario.ttx_enabled ? 'On' : 'Off'}
                          </Badge>
                        )}
                        {typeof scenario.is_prepared === 'boolean' && (
                          <Badge
                            className={
                              scenario.is_prepared
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 font-medium'
                                : 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300 font-medium'
                            }
                          >
                            {scenario.is_prepared ? 'Prepared' : 'Not Prepared'}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <CardHeader>
                      <CardTitle className="text-xl line-clamp-1">{scenario.name}</CardTitle>
                      <CardDescription className="line-clamp-2 h-12">
                        {scenario.description}
                      </CardDescription>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">
                          {scenario.category_name}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-1">
                          <span className="text-yellow-500">★</span>
                          <span className="font-medium">4.5</span>
                          <span className="text-muted-foreground text-xs">(93)</span>
                        </div>
                        <div className="text-muted-foreground flex items-center gap-2">
                          <Trophy className="h-4 w-4 text-warning" />
                          <span className="font-medium">{scenario.points ?? 0} points</span>
                        </div>
                      </div>
                      {typeof scenario.ttx_enabled === 'boolean' && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          TTX:{' '}
                          <span
                            className={
                              scenario.ttx_enabled
                                ? 'text-amber-600 dark:text-amber-400 font-medium'
                                : ''
                            }
                          >
                            {scenario.ttx_enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      )}
                      {typeof scenario.is_prepared === 'boolean' && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          Approval prerequisite:{' '}
                          <span
                            className={
                              scenario.is_prepared
                                ? 'text-emerald-600 dark:text-emerald-400 font-medium'
                                : 'text-rose-600 dark:text-rose-400 font-medium'
                            }
                          >
                            {scenario.is_prepared ? 'Prepared' : 'Prepare first'}
                          </span>
                        </div>
                      )}
                    </CardContent>
                    <CardFooter className="flex flex-col gap-2 border-t pt-4">
                      <div className="flex flex-wrap gap-2 w-full">
                        <Button size="sm" variant="outline" className="flex-1 min-w-[120px]" asChild>
                          <Link to={`/scenarios/${scenario.id}/edit`}>Edit</Link>
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 min-w-[120px] bg-blue-600 hover:bg-blue-700 text-white"
                          asChild
                        >
                          <Link to={`/scenarios/${scenario.id}/detail`}>View Details</Link>
                        </Button>
                      </div>
                      {scenario.is_prepared && (
                        <div className="flex flex-wrap gap-2 w-full">
                          {scenario.is_approved ? (
                            <Button
                              size="sm"
                              variant="destructive"
                              className="flex-1 min-w-[120px]"
                              onClick={() => handleUnapprove(scenario.id)}
                              disabled={unapprovingId === scenario.id}
                            >
                              {unapprovingId === scenario.id ? 'Unapproving...' : 'Unapprove'}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              className="flex-1 min-w-[120px] bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => handleApprove(scenario.id)}
                              disabled={approvingId === scenario.id}
                            >
                              {approvingId === scenario.id ? 'Approving...' : 'Approve'}
                            </Button>
                          )}
                        </div>
                      )}
                    </CardFooter>
                  </Card>
                ))}
              </div>
              <Pagination
                totalPages={totalPages}
                currentPage={currentPage}
                onNext={() => handlePageClick(nextPage)}
                onPrev={() => handlePageClick(prevPage)}
                onFirst={() => navigate(`${pathname}?${createQueryString('page', '1')}`)}
                onLast={() => navigate(`${pathname}?${createQueryString('page', String(totalPages))}`)}
              />
            </>
          ) : (
            <div className="col-span-full text-center py-12">
              <div className="text-4xl mb-2">🔍</div>
              <h3 className="text-lg font-medium">No scenarios found</h3>
              <p className="text-muted-foreground">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      )}
    </>
  );
}
